import { useState } from 'react';
import { createRoot } from 'react-dom/client';
import {
    VerticalContainer,
    Button,
    Modal,
    Card,
    InlineAlert,
} from '@/components';
import { R } from '@/Resources.resx';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ConsultationTypeEnum } from '@/shared/enums';
import {
    AttachmentsList,
    UploadFilesButton,
    useUploadFiles,
} from '@/hooks/upload-files';
import { useCreateDietitianUploadConsultation } from '@/features/fileUpload/lib/useCreateDietitianUploadConsultation';

export function FileUploadDietitianApp(containerQuery: string) {
    const elm = document.querySelector(containerQuery);
    const root = createRoot(elm);
    const queryClient = new QueryClient();
    root.render(
        <QueryClientProvider client={queryClient}>
            <FileUpload />
        </QueryClientProvider>,
    );
}

const FileUpload = () => {
    const { handleFileChange, files, removeFileFromList } = useUploadFiles({
        senderType: 'PATIENT',
        fileConstraints: {
            maxFileSizeMb: 30,
        },
    });

    const fileUIDs = files.map((file) => file.guid);

    const allFilesUploaded = files.every((file) => file.isUploaded);

    const { mutate, status, reset, errorMessage } =
        useCreateDietitianUploadConsultation();

    const [uploadFailedModal, setUploadFailedModal] = useState<boolean>(false);

    const closeUploadFailedModal = () => {
        setUploadFailedModal(false);
    };

    const handleClickSendFiles = () => {
        mutate(
            {
                FileUids: fileUIDs,
                ConsultationType:
                    ConsultationTypeEnum.DietitianUploadAttachmentMessage,
                Subject: 'Send files to the dietitian',
                Message: R.DietitianUploadAttachmentConsultationMessage,
            },
            {
                onError: () => {
                    reset();
                    setUploadFailedModal(true);
                },
            },
        );
    };

    return (
        <>
            {status !== 'success' && (
                <Card className="max-w-180">
                    <h2 className="mb-4">{R.SendFilesToDietitianHeader}</h2>
                    <span>{R.AllowedFileFormatsDietitian}</span>
                    {files.length > 0 && (
                        <VerticalContainer>
                            <AttachmentsList
                                files={files}
                                onRemoveCallback={removeFileFromList}
                            />
                        </VerticalContainer>
                    )}
                    <div className="flex justify-center">
                        <span className="mt-4 w-48">
                            <UploadFilesButton
                                onFileChange={handleFileChange}
                                accept="image/jpeg,image/png,image/gif,.pdf,.mov,.mp4"
                            />
                        </span>
                    </div>

                    <div className="flex justify-end">
                        <Button
                            variant="primary"
                            disabled={!(files.length > 0) || !allFilesUploaded}
                            className={
                                status === 'pending'
                                    ? 'button--loading mt-4'
                                    : 'mt-4'
                            }
                            onClick={handleClickSendFiles}
                        >
                            {R.UploadFilesButton}
                        </Button>
                    </div>
                </Card>
            )}
            {status === 'success' && (
                <Card className="max-w-120">
                    <div>
                        <h2>{R.UploadFilesDietitianSuccess}</h2>
                        <footer className="mt-12 text-right">
                            <Button
                                variant="primary"
                                onClick={() => {
                                    window.location.href = '/';
                                }}
                            >
                                {R.UploadFilesDietitianFinish}
                            </Button>
                        </footer>
                    </div>
                </Card>
            )}
            {uploadFailedModal && (
                <Modal onClose={closeUploadFailedModal}>
                    {status === 'error' && (
                        <InlineAlert
                            onClose={closeUploadFailedModal}
                            errorMessage={errorMessage}
                        />
                    )}
                </Modal>
            )}
        </>
    );
};
